// EditorSection — a labeled group of everyday sub-controls inside a block pane.
// WL-011. The "Basic" half of the row-39 split: a 10px/700 uppercase #8F38EF
// overline + thin rule (the deck's ".ba-div" canonical anchor, state 09) over a
// vertically-stacked control group. Pairs with <AdvancedOptions> for the pro half.
//
// Set `heading` to render the overline; omit it for an unlabeled group.
// Accessibility: the overline is a real heading (role/aria) associated with the
// group via aria-labelledby so screen-reader users get the section structure.

import React, { useId } from "react";
import styled from "styled-components";
import { fontFamily, brand } from "../../../theme/tokens";

const OVERLINE = brand.primaryActive; // #8F38EF — darkened primary (AA on white)
const RULE = "#E9DBFC"; // faint purple hairline (deck ".ba-div" anchor)

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$dense ? "4px" : "6px")};
  box-sizing: border-box;
  & * { box-sizing: border-box; }
`;

const Overline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`;

const OverlineText = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${OVERLINE};
  flex: none;
  font-family: ${fontFamily};
`;

const Rule = styled.span`
  flex: 1;
  height: 1px;
  background: ${RULE};
`;

const EditorSection = ({ heading, dense = false, children, ...rest }) => {
  const headingId = useId();
  return (
    <Wrap $dense={dense} aria-labelledby={heading ? headingId : undefined} {...rest}>
      {heading && (
        <Overline>
          <OverlineText id={headingId} role="heading" aria-level={3}>
            {heading}
          </OverlineText>
          <Rule aria-hidden="true" />
        </Overline>
      )}
      {children}
    </Wrap>
  );
};

export default EditorSection;
